// One-shot board SFX — 돌 놓기(place) and 돌 뒤집기(flip), triggered from
// MatchScreen on every committed move (player or AI, see useMatch's
// commitMove). Each play is an independent BufferSource on the shared
// context (context.ts) so overlapping triggers (fast clicking, or an AI move
// landing right after a player one) don't cut each other off.

import { getAudioCtx } from "./context.ts";

export type SfxId = "place" | "flip";

interface SfxDef {
  src: string;
  // Pre-limiter gain multiplier — flip's source clip sits much quieter (RMS)
  // than place's, so it gets lost under BGM; boosted here rather than just
  // raising volume, which would take place and the music with it.
  gain: number;
}

const SFX: Record<SfxId, SfxDef> = {
  place: { src: "/assets/audio/sfx-place.mp3", gain: 1 },
  flip: { src: "/assets/audio/sfx-flip.mp3", gain: 2.2 },
};

const buffers = new Map<SfxId, Promise<AudioBuffer>>();
let limiter: DynamicsCompressorNode | null = null;

// A shared limiter downstream of every SFX gain boost, so pushing flip's
// gain past 1 can't hard-clip its already near-0dBFS peaks into crackle.
function getLimiter(ctx: AudioContext, master: GainNode): DynamicsCompressorNode {
  if (!limiter) {
    limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 6;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.05;
    limiter.connect(master);
  }
  return limiter;
}

function loadBuffer(ctx: AudioContext, id: SfxId): Promise<AudioBuffer> {
  let p = buffers.get(id);
  if (!p) {
    p = fetch(SFX[id].src)
      .then((r) => r.arrayBuffer())
      .then((arr) => ctx.decodeAudioData(arr));
    buffers.set(id, p);
  }
  return p;
}

/** Plays a one-shot SFX. `flipCount` (only meaningful for "flip") nudges the
 *  playback rate up a little per disc flipped, so a big cascade reads as
 *  slightly more urgent than a single flip — pass `view.lastFlipped.length`. */
export function playSfx(id: SfxId, opts?: { flipCount?: number }): void {
  const { ctx, master } = getAudioCtx();
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  loadBuffer(ctx, id).then((buffer) => {
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    if (id === "flip" && opts?.flipCount) {
      src.playbackRate.value = 1 + Math.min(opts.flipCount, 12) * 0.015;
    }
    const gain = ctx.createGain();
    gain.gain.value = SFX[id].gain;
    src.connect(gain).connect(getLimiter(ctx, master));
    src.start();
  });
}
